/**
 * Ranked war payouts & war history (website roadmap S21 — Torn Intel's payouts, TornBot's payout calculator, TornStats'
 * wars/{id}): your faction's finished ranked wars, member by member, kept for the faction; a payout calculator; the ledger
 * across wars. Pure, browser-safe — one copy for the server (apps/api/src/war-records.ts), the website (Faction → Wars)
 * and the extension's war ledger (core/war.ts re-exports the ledger).
 *
 * Where a war record comes from (the server, with the keys members gave the Discord bot — own faction only):
 *  - v2 /faction/rankedwars — the faction's ranked wars (the key owner's own faction);
 *  - v2 /faction/{warId}/rankedwarreport — Torn's report: both sides, every member's war hits and score, rank and rewards;
 *  - v2 /faction/attacks?from&to (the faction's "API access") — the war's attack log, counted here into each of OUR
 *    members' respect, fair fight, assists, losses, hits outside the war and hits taken from the enemy. Only those totals
 *    are kept, never the attacks themselves.
 *
 * Terms: reading your faction with a member's bot key and handing it to mates for an hour, in memory, is in the table
 * since version 5. KEEPING the records for the faction is new: only with a key whose owner agreed to WAR_RECORDS_TERMS.
 * The payout calculator runs in the browser; its settings go only into a link a member copies (no public links: the
 * link shows nothing to anyone who isn't signed in as a member of that faction).
 */

import type { WarReport, WarReportMember } from './war';

/** The terms version that keeps a faction's war records on the server (beyond the hour in memory). */
export const WAR_RECORDS_TERMS = 10;
/** How long a war record is kept (from WAR_RECORDS_TERMS), from the war's end. */
export const WAR_RECORDS_KEEP_DAYS = 400;
/** Without the terms: what was read is kept this long, in memory. */
export const WAR_RECORDS_MEMORY_MS = 60 * 60_000;

type Obj = Record<string, unknown>;
const obj = (x: unknown): Obj | null => (x && typeof x === 'object' && !Array.isArray(x) ? (x as Obj) : null);
const num = (x: unknown): number | null => (typeof x === 'number' && Number.isFinite(x) ? x : typeof x === 'string' && /^-?\d+(\.\d+)?$/.test(x) ? Number(x) : null);
const str = (x: unknown): string | null => (typeof x === 'string' && x.trim() ? x.trim() : null);
const list = (x: unknown): unknown[] => (Array.isArray(x) ? x : Object.values(obj(x) ?? {}));
const r2 = (n: number) => Math.round(n * 100) / 100;

// ---------------------------------------------------------------- the attack log

/** One line of v2 /faction/attacks, as far as the count needs it. */
export interface WarAttack {
  id: number;
  started: number;
  ended: number;
  attacker: { id: number; name: string | null; factionId: number | null } | null;
  defender: { id: number | null; name: string | null; factionId: number | null };
  result: string;
  respect: number;
  fairFight: number | null;
}

const sideOf = (x: unknown): { id: number | null; name: string | null; factionId: number | null } => {
  const o = obj(x);
  return { id: num(o?.id) ?? num(x), name: str(o?.name), factionId: num(obj(o?.faction)?.id) ?? num(o?.faction_id) ?? num(o?.faction) };
};

/** v2 /faction/attacks (or a v1-ish map): oldest first. */
export function readWarAttacks(body: unknown): WarAttack[] {
  const out: WarAttack[] = [];
  for (const raw of list(obj(body)?.attacks)) {
    const a = obj(raw);
    const id = num(a?.id);
    if (!a || !id) continue;
    const att = sideOf(a.attacker ?? (a.attacker_id ? { id: a.attacker_id, name: a.attacker_name, faction_id: a.attacker_faction } : null));
    const def = sideOf(a.defender ?? (a.defender_id ? { id: a.defender_id, name: a.defender_name, faction_id: a.defender_faction } : null));
    const started = num(a.started) ?? num(a.timestamp_started) ?? 0;
    out.push({
      id,
      started,
      ended: num(a.ended) ?? num(a.timestamp_ended) ?? started,
      attacker: att.id ? { id: att.id, name: att.name, factionId: att.factionId } : null,
      defender: def,
      result: str(a.result) ?? '',
      respect: num(a.respect_gain) ?? num(a.respect) ?? 0,
      fairFight: num(obj(a.modifiers)?.fair_fight),
    });
  }
  return out.sort((x, y) => x.started - y.started || x.id - y.id);
}

/** Results Torn counts as a won attack (as the bot's war hits count them, war.ts). */
const WON = /^(attacked|mugged|hospitali[sz]ed|arrested|looted|special|bounty)$/i;
const LOST = /^(lost|stalemate|timeout|escape|interrupted)$/i;

/** Running totals for one of our members over the war's attack log. */
export interface WarCountMember {
  id: number;
  name: string;
  /** Won attacks on the enemy during the war (should match Torn's war hits). */
  warHits: number;
  /** Respect from those. */
  respect: number;
  /** Sum and count of their fair fights (for the average). */
  ffSum: number;
  ffN: number;
  assists: number;
  /** Attacks on the enemy they lost (or stalemated, timed out, escaped from). */
  losses: number;
  /** Won attacks on anyone else during the war. */
  outside: number;
  /** Times the enemy beat them during the war. */
  bled: number;
}

/**
 * The count of one war's attack log, read page by page (`cursor` says where the next page starts). It holds totals only —
 * never an attack, not even an attack id: a page's last second is left for the next page (which starts at that second),
 * so nothing has to remember which of that second's attacks were already counted.
 */
export interface WarCount {
  warId: number;
  us: number;
  enemy: number;
  start: number;
  end: number;
  /** Read up to this `started` (unix s): every line before it is counted, none at or after it; the next page starts here. */
  cursor: number;
  /**
   * Only in counts stored before v0.127.0: ids already counted that started exactly at `cursor`. Honoured once (the next
   * page skips them), then gone — new counts never have it. Never written to disk again (`warCountToKeep`).
   */
  edge?: number[];
  /** Attack log lines counted. */
  lines: number;
  /** The whole window is read. */
  done: boolean;
  members: Record<string, WarCountMember>;
}

/** Torn lists an attack by when it started: the log is read from a little before the war (attacks ending inside it count). */
export const WAR_LOG_LEAD_S = 600;
/** Lines on one page of v2 /faction/attacks. */
export const WAR_LOG_PAGE = 100;

export function newWarCount(war: { id: number; start: number; end: number }, us: number, enemy: number): WarCount {
  return { warId: war.id, us, enemy, start: war.start, end: war.end, cursor: war.start - WAR_LOG_LEAD_S, lines: 0, done: false, members: {} };
}

/** What of a count may be stored: the totals and where to go on — never an attack id (an old count's `edge` is dropped). */
export function warCountToKeep(c: WarCount): WarCount {
  if (!('edge' in c)) return c;
  const { edge: _edge, ...rest } = c;
  return rest;
}

/** The query for the next page of the war's attack log (v2 /faction/attacks). */
export function warLogQuery(c: WarCount): { from: number; to: number; sort: 'ASC'; limit: number } {
  return { from: c.cursor, to: c.end, sort: 'ASC', limit: WAR_LOG_PAGE };
}

/**
 * Counts one page of the attack log into `c` (each attack once) and moves the cursor. A short page (fewer than
 * WAR_LOG_PAGE) means the window is read: every line is counted. A full page: the lines before its last second are
 * counted and the cursor moves to that second (the next page starts there and brings that second's lines again, whole).
 * A full page all in the cursor's second is counted whole and the cursor steps past it (no endless loop; Torn's
 * pages can't reach beyond 100 lines in one second). Returns how many new lines were counted.
 */
export function countWarPage(c: WarCount, page: readonly WarAttack[]): number {
  // A count stored before v0.127.0: its edge ids (already counted, at the cursor's second) are skipped this once.
  const old = new Set(c.edge ?? []);
  if (c.edge) delete c.edge;
  const at = page.filter((a) => a.started >= c.cursor && !(a.started === c.cursor && old.has(a.id)));
  const full = page.length >= WAR_LOG_PAGE;
  const last = page[page.length - 1];
  const stepPast = full && !!last && last.started <= c.cursor;
  const upTo = full && last && !stepPast ? last.started : Infinity;
  let added = 0;
  const get = (id: number, name: string | null) => {
    const k = String(id);
    const m = c.members[k] ?? (c.members[k] = { id, name: name ?? `#${id}`, warHits: 0, respect: 0, ffSum: 0, ffN: 0, assists: 0, losses: 0, outside: 0, bled: 0 });
    if (name) m.name = name;
    return m;
  };
  for (const a of at) {
    if (a.started >= upTo) continue;
    added++;
    c.lines++;
    if (a.ended < c.start || a.ended > c.end) continue;
    const won = WON.test(a.result) || (a.respect > 0 && !/^assist$/i.test(a.result));
    if (a.attacker && a.attacker.factionId === c.us) {
      const m = get(a.attacker.id, a.attacker.name);
      if (a.defender.factionId === c.enemy) {
        if (/^assist$/i.test(a.result)) m.assists++;
        else if (won) {
          m.warHits++;
          m.respect = r2(m.respect + Math.max(0, a.respect));
          if (a.fairFight !== null) {
            m.ffSum = r2(m.ffSum + a.fairFight);
            m.ffN++;
          }
        } else if (LOST.test(a.result)) m.losses++;
      } else if (won) m.outside++;
    }
    if (a.attacker && a.attacker.factionId === c.enemy && a.defender.factionId === c.us && a.defender.id && won) get(a.defender.id, a.defender.name).bled++;
  }
  if (!full) {
    // A short page: all read.
    if (last && last.started > c.cursor) c.cursor = last.started;
    c.done = true;
  } else if (stepPast) c.cursor++;
  else c.cursor = upTo;
  if (c.cursor > c.end) c.done = true;
  return added;
}

// ---------------------------------------------------------------- a war record

export interface WarRecordSide {
  id: number;
  name: string;
  score: number;
  attacks: number;
  rank: { before: string | null; after: string | null } | null;
  rewards: { respect: number | null; points: number | null; items: Array<{ id: number; name: string; quantity: number }> } | null;
}

export interface WarRecordMember {
  id: number;
  name: string;
  level: number | null;
  /** Torn's war hits (the report's attacks). */
  hits: number;
  /** Torn's war score (respect earned in the war). */
  score: number;
  /** From the attack log, once counted (else null): respect from war hits, average fair fight, assists, losses, outside hits, hits taken. */
  respect: number | null;
  avgFairFight: number | null;
  assists: number | null;
  losses: number | null;
  outside: number | null;
  bled: number | null;
}

export interface WarRecord {
  id: number;
  start: number;
  end: number;
  winner: number | null;
  forfeit: boolean;
  us: WarRecordSide;
  enemy: WarRecordSide;
  /** Our members (Torn's report, plus anyone the log shows hitting who isn't on it), most score first. */
  members: WarRecordMember[];
  /** The enemy's members as Torn's report lists them. */
  enemyMembers: WarReportMember[];
  /** The attack log is counted in full. */
  counted: boolean;
  /** How far the count has got: lines read, and up to when (unix s). */
  log: { lines: number; upTo: number } | null;
}

const sideFrom = (s: WarReport['factions'][number]): WarRecordSide => ({ id: s.id, name: s.name, score: s.score, attacks: s.attacks, rank: s.rank ?? null, rewards: s.rewards ?? null });

/** Torn's report (both sides) seen from `us`, with the attack log's count when there is one. Null when `us` isn't on it. */
export function warRecordOf(report: WarReport, us: number, info: { start: number; end: number }, count: WarCount | null): WarRecord | null {
  const ours = report.factions.find((f) => f.id === us);
  const theirs = report.factions.find((f) => f.id !== us);
  if (!ours || !theirs) return null;
  const counted = !!count && count.done;
  const fromLog = (id: number): Pick<WarRecordMember, 'respect' | 'avgFairFight' | 'assists' | 'losses' | 'outside' | 'bled'> => {
    const m = count?.members[String(id)];
    if (!count) return { respect: null, avgFairFight: null, assists: null, losses: null, outside: null, bled: null };
    return { respect: r2(m?.respect ?? 0), avgFairFight: m && m.ffN ? r2(m.ffSum / m.ffN) : null, assists: m?.assists ?? 0, losses: m?.losses ?? 0, outside: m?.outside ?? 0, bled: m?.bled ?? 0 };
  };
  const members: WarRecordMember[] = ours.members.map((m) => ({ id: m.id, name: m.name, level: m.level, hits: m.attacks, score: m.score, ...fromLog(m.id) }));
  const listed = new Set(members.map((m) => m.id));
  for (const m of Object.values(count?.members ?? {})) {
    if (listed.has(m.id) || !(m.warHits || m.assists || m.outside || m.bled || m.losses)) continue;
    members.push({ id: m.id, name: m.name, level: null, hits: 0, score: 0, ...fromLog(m.id) });
  }
  members.sort((a, b) => b.score - a.score || b.hits - a.hits || a.name.localeCompare(b.name));
  return {
    id: report.id,
    start: report.start ?? info.start,
    end: report.end ?? info.end,
    winner: report.winner,
    forfeit: report.forfeit,
    us: sideFrom(ours),
    enemy: sideFrom(theirs),
    members,
    enemyMembers: theirs.members,
    counted,
    log: count ? { lines: count.lines, upTo: Math.min(count.cursor, count.end) } : null,
  };
}

/** The record without one player ("Delete my data"): their rows on both sides and in the count go. */
export function warReportWithout(report: WarReport, playerId: number): WarReport {
  if (!report.factions.some((f) => f.members.some((m) => m.id === playerId))) return report;
  return { ...report, factions: report.factions.map((f) => ({ ...f, members: f.members.filter((m) => m.id !== playerId) })) };
}

export function warCountWithout(c: WarCount, playerId: number): WarCount {
  if (!c.members[String(playerId)]) return c;
  const members = { ...c.members };
  delete members[String(playerId)];
  return { ...c, members };
}

// ---------------------------------------------------------------- what the server answers

/** One war in the faction's list (Faction → Wars). */
export interface WarListEntry {
  id: number;
  start: number;
  /** 0 while it runs. */
  end: number;
  winner: number | null;
  us: { id: number; name: string; score: number };
  enemy: { id: number; name: string; score: number };
  /** Kept on the server for the faction (WAR_RECORDS_TERMS). */
  kept: boolean;
  /** Torn's report is in, and the attack log is counted in full. */
  reported: boolean;
  counted: boolean;
}

/** GET /api/faction/wars */
export interface FactionWarsAnswer {
  wars: WarListEntry[];
  /** When the list was read from Torn (ms), if it was. */
  readAt: number | null;
  /** Records are kept for this faction (a member whose bot key reads it agreed to WAR_RECORDS_TERMS). */
  kept: boolean;
  /** Days kept; 0 = no end date (terms KEEP_ALL_TERMS, "No age limits"). */
  keepDays: number;
  terms: number;
  problem: string | null;
}

/** GET /api/faction/wars/:id */
export interface FactionWarAnswer {
  war: WarRecord | null;
  kept: boolean;
  /** The attack log is still being counted (ask again in a little while for more). */
  counting: boolean;
  /** Why the attack log can't be counted (no key with the faction's API access), or the report read failed. */
  problem: string | null;
}

// ---------------------------------------------------------------- the ledger across wars (shared with the extension)

/** What the ledger needs of a war and its members (the website's WarRecord and the extension's WarSummary both fit). */
export interface LedgerMemberLike {
  id: number;
  name: string;
  hits: number;
  outside?: number | null;
  bled?: number | null;
}
export interface LedgerWarLike {
  id: number;
  start: number;
  counted: boolean;
  members: readonly LedgerMemberLike[];
}

export interface MemberLedger {
  id: number;
  name: string;
  /** Wars they were in the faction for (on the report), newest first: hits each. */
  hits: number[];
  wars: number;
  /** How sure the rating is, from how many wars they've been in. */
  confidence: 'low' | 'medium' | 'high';
  /** Share of those wars with at least one hit, 0–1. */
  participation: number;
  avgHits: number;
  /** Average of their last 5 wars. */
  recentAvg: number;
  /** Wars in a row, newest first, with no hit. */
  zeroStreak: number;
  /** Their share of the faction's hits over the wars they were in, 0–1. */
  share: number;
  avgBled: number | null;
  avgOutside: number | null;
  rating: number;
  grade: 'A' | 'B' | 'C' | 'D' | 'F';
  /** Their last 3 wars against the ones before ('new': not enough wars yet). */
  form: 'improving' | 'steady' | 'slipping' | 'new';
  flags: string[];
}

/**
 * Each member's record over the wars kept (newest first). `settleWars`: until a member has been in that many wars, their
 * participation and hits lean towards the faction's average member (as if they'd also had `settleWars` average wars), so
 * a newcomer with one bad war isn't judged like a long-timer with many. (From the extension's war ledger, v0.7x.)
 */
export function warLedger(wars: readonly LedgerWarLike[], settleWars = 5): MemberLedger[] {
  const sorted = [...wars].sort((a, b) => b.start - a.start);
  const factionHits = new Map(sorted.map((w) => [w.id, w.members.reduce((n, m) => n + m.hits, 0)]));
  const factionAvg = new Map(sorted.map((w) => [w.id, w.members.length ? factionHits.get(w.id)! / w.members.length : 0]));
  const ids = new Map<number, string>();
  for (const w of sorted) for (const m of w.members) if (!ids.has(m.id)) ids.set(m.id, m.name);
  // The faction's average participation (member-wars with a hit), the newcomer's starting point.
  let memberWars = 0;
  let withHits = 0;
  for (const w of sorted)
    for (const m of w.members) {
      memberWars++;
      if (m.hits > 0) withHits++;
    }
  const priorPart = memberWars ? withHits / memberWars : 0.7;
  const k = Math.max(0, settleWars);
  const out: MemberLedger[] = [];
  for (const [id, name] of ids) {
    const rows = sorted.map((w) => ({ w, m: w.members.find((x) => x.id === id) })).filter((r): r is { w: LedgerWarLike; m: LedgerMemberLike } => !!r.m);
    const hits = rows.map((r) => r.m.hits);
    const wars = rows.length;
    const participation = wars ? hits.filter((x) => x > 0).length / wars : 0;
    const avgHits = wars ? hits.reduce((a, b) => a + b, 0) / wars : 0;
    const recent = hits.slice(0, 5);
    const recentAvg = recent.length ? recent.reduce((a, b) => a + b, 0) / recent.length : 0;
    let zeroStreak = 0;
    for (const x of hits) {
      if (x > 0) break;
      zeroStreak++;
    }
    const theirHits = hits.reduce((a, b) => a + b, 0);
    const allHits = rows.reduce((n, r) => n + (factionHits.get(r.w.id) ?? 0), 0);
    const share = allHits ? theirHits / allHits : 0;
    // How they compare with the average member in the same wars (1 = average).
    const avgOfAvg = rows.length ? rows.reduce((n, r) => n + (factionAvg.get(r.w.id) ?? 0), 0) / rows.length : 0;
    const vsAverage = avgOfAvg ? avgHits / avgOfAvg : 0;
    const counted = rows.filter((r) => r.w.counted);
    const avgBled = counted.length ? counted.reduce((n, r) => n + (r.m.bled ?? 0), 0) / counted.length : null;
    const avgOutside = counted.length ? counted.reduce((n, r) => n + (r.m.outside ?? 0), 0) / counted.length : null;
    // Rating: showing up (60), pulling their weight against the average member (40, capped at 2× average), minus 10 per war in a row without a hit.
    // Leaning towards the average member while they're new (k "average" wars added in).
    const partAdj = (wars * participation + k * priorPart) / Math.max(1, wars + k);
    const vsAdj = (wars * Math.min(2, vsAverage) + k * 1) / Math.max(1, wars + k);
    const settled = wars + k ? wars / (wars + k) : 1;
    const rating = Math.max(0, Math.min(100, Math.round(60 * partAdj + 20 * vsAdj - 10 * zeroStreak * settled)));
    const confidence = wars < Math.max(3, k) ? 'low' : wars < Math.max(10, 3 * k) ? 'medium' : 'high';
    const grade = rating >= 85 ? 'A' : rating >= 70 ? 'B' : rating >= 55 ? 'C' : rating >= 40 ? 'D' : 'F';
    // Form: their last 3 wars against the ones before.
    const last3 = hits.slice(0, 3);
    const before = hits.slice(3);
    const a3 = last3.reduce((a, b) => a + b, 0) / Math.max(1, last3.length);
    const ab = before.reduce((a, b) => a + b, 0) / Math.max(1, before.length);
    const form = !before.length ? 'new' : a3 > ab * 1.2 + 0.5 ? 'improving' : a3 < ab * 0.8 - 0.5 ? 'slipping' : 'steady';
    const flags: string[] = [];
    if (zeroStreak) flags.push(zeroStreak === 1 ? 'no hits last war' : `${zeroStreak} wars without a hit`);
    if (wars >= Math.max(3, k) && participation < 0.6) flags.push('low participation');
    const factionBled = counted.length ? counted.reduce((n, r) => n + r.w.members.reduce((a, m) => a + (m.bled ?? 0), 0) / Math.max(1, r.w.members.length), 0) / counted.length : null;
    if (avgBled !== null && factionBled !== null && factionBled > 0 && avgBled >= factionBled * 2 && avgBled >= 3) flags.push('hit a lot by the enemy');
    if (avgOutside !== null && avgOutside > Math.max(5, avgHits)) flags.push('more outside hits than war hits');
    out.push({ id, name, hits, wars, confidence, participation, avgHits: Math.round(avgHits * 10) / 10, recentAvg: Math.round(recentAvg * 10) / 10, zeroStreak, share, avgBled: avgBled === null ? null : Math.round(avgBled * 10) / 10, avgOutside: avgOutside === null ? null : Math.round(avgOutside * 10) / 10, rating, grade, form, flags });
  }
  return out.sort((a, b) => b.rating - a.rating || b.avgHits - a.avgHits);
}

/** Who stands out in the ledger. */
export function warHighlights(l: readonly MemberLedger[], latest: LedgerWarLike | null): Array<{ label: string; name: string; why: string }> {
  const out: Array<{ label: string; name: string; why: string }> = [];
  const top = latest ? [...latest.members].sort((a, b) => b.hits - a.hits)[0] : undefined;
  if (top?.hits) out.push({ label: 'Top in the last war', name: top.name, why: `${top.hits} hits` });
  const carry = [...l].filter((m) => m.wars >= 2).sort((a, b) => b.share - a.share)[0];
  if (carry) out.push({ label: 'Carries the most', name: carry.name, why: `${Math.round(carry.share * 100)}% of the faction’s hits` });
  const improved = [...l].filter((m) => m.form === 'improving').sort((a, b) => b.recentAvg - b.avgHits - (a.recentAvg - a.avgHits))[0];
  if (improved) out.push({ label: 'Most improved', name: improved.name, why: `${improved.recentAvg} a war lately vs ${improved.avgHits} overall` });
  const risk = [...l].filter((m) => m.wars >= 2).sort((a, b) => a.rating - b.rating)[0];
  if (risk && risk.rating < 55) out.push({ label: 'Most at risk', name: risk.name, why: `rating ${risk.rating}${risk.flags.length ? ` — ${risk.flags.join(', ')}` : ''}` });
  return out;
}

// ---------------------------------------------------------------- the payout calculator (in the browser)

/** What a member is paid for. */
export type PayoutBasis = 'hits' | 'respect' | 'score' | 'equal';
export const PAYOUT_BASES: ReadonlyArray<{ id: PayoutBasis; label: string }> = [
  { id: 'hits', label: 'War hits' },
  { id: 'respect', label: 'Respect' },
  { id: 'score', label: 'War score' },
  { id: 'equal', label: 'Equal shares' },
];

export interface PayoutRules {
  basis: PayoutBasis;
  /** 'pool': share a total out by the basis; 'rate': a fixed amount per hit / respect / score point (no pool). */
  mode: 'pool' | 'rate';
  /** The money to share out ('pool'). */
  pool: number;
  /** $ per unit ('rate'). */
  rate: number;
  /** % of the pool the faction keeps first ('pool'). */
  factionCut: number;
  /** An assist counts as this many hits (0–1) for the 'hits' basis — only once the attack log is counted. */
  assistValue: number;
  /** Members with fewer war hits get nothing. */
  minHits: number;
  /** Every qualifying member gets at least this; no one more than `maxPay` (0: no cap). */
  minPay: number;
  maxPay: number;
  /** A bonus for the top `topCount` members by the basis (out of the pool first, in 'pool' mode). */
  topBonus: number;
  topCount: number;
}

export const DEFAULT_PAYOUT_RULES: PayoutRules = { basis: 'hits', mode: 'pool', pool: 0, rate: 0, factionCut: 0, assistValue: 0, minHits: 1, minPay: 0, maxPay: 0, topBonus: 0, topCount: 1 };

const clampN = (x: unknown, lo: number, hi: number, dflt: number): number => {
  const n = typeof x === 'string' ? Number(x.replace(/[$,\s]/g, '')) : typeof x === 'number' ? x : NaN;
  return Number.isFinite(n) ? Math.min(hi, Math.max(lo, n)) : dflt;
};

/** Anything (a form, a shared link) → valid rules. */
export function cleanPayoutRules(x: unknown): PayoutRules {
  const o = obj(x) ?? {};
  const d = DEFAULT_PAYOUT_RULES;
  const basis = PAYOUT_BASES.some((b) => b.id === o.basis) ? (o.basis as PayoutBasis) : d.basis;
  const MAX = 1e14;
  return {
    basis,
    mode: o.mode === 'rate' ? 'rate' : 'pool',
    pool: Math.round(clampN(o.pool, 0, MAX, d.pool)),
    rate: clampN(o.rate, 0, MAX, d.rate),
    factionCut: clampN(o.factionCut, 0, 100, d.factionCut),
    assistValue: clampN(o.assistValue, 0, 1, d.assistValue),
    minHits: Math.round(clampN(o.minHits, 0, 10_000, d.minHits)),
    minPay: Math.round(clampN(o.minPay, 0, MAX, d.minPay)),
    maxPay: Math.round(clampN(o.maxPay, 0, MAX, d.maxPay)),
    topBonus: Math.round(clampN(o.topBonus, 0, MAX, d.topBonus)),
    topCount: Math.round(clampN(o.topCount, 1, 100, d.topCount)),
  };
}

/** What the calculator needs of a member. */
export type PayoutMember = Pick<WarRecordMember, 'id' | 'name' | 'hits' | 'score'> & { respect: number | null; assists: number | null };

export interface PayoutRow {
  id: number;
  name: string;
  /** Their amount of the basis (hits incl. assists' worth, respect, score; 1 for equal shares). */
  units: number;
  /** Whether they qualify (enough war hits). */
  qualified: boolean;
  /** Their share before the bonus, the bonus, and the whole payout (whole dollars). */
  base: number;
  bonus: number;
  pay: number;
  /** Their payout was raised to the minimum or cut to the maximum. */
  clamp: 'min' | 'max' | null;
  /** Share of everything paid out, 0–1. */
  share: number;
}

export interface PayoutResult {
  rows: PayoutRow[];
  /** Everything paid to members. */
  total: number;
  /** What the faction keeps ('pool': the cut and anything left over from rounding or caps). */
  kept: number;
  /** 'pool': the money shared out after the faction's cut. */
  shared: number;
  /** Plain-word problems ("the minimums add up to more than the pool"). */
  notes: string[];
}

/** A member's amount of the basis. */
export function payoutUnits(m: PayoutMember, r: Pick<PayoutRules, 'basis' | 'assistValue'>): number {
  if (r.basis === 'equal') return 1;
  if (r.basis === 'score') return Math.max(0, m.score);
  if (r.basis === 'respect') return Math.max(0, m.respect ?? m.score);
  return Math.max(0, m.hits + (m.assists ?? 0) * r.assistValue);
}

/**
 * The payout for each member under `rules`. Pool: the faction's cut first, then the top bonuses, then the rest shared by
 * the basis among qualifying members — each held between the minimum and the maximum (what a cap frees goes to the
 * others; minimums come out of the others' shares). Rate: units × rate, held between min and max, plus the bonus.
 * Whole dollars, rounded down (the remainder stays with the faction).
 */
export function payouts(members: readonly PayoutMember[], rulesIn: PayoutRules): PayoutResult {
  const rules = cleanPayoutRules(rulesIn);
  const notes: string[] = [];
  const rows: PayoutRow[] = members.map((m) => {
    const units = payoutUnits(m, rules);
    return { id: m.id, name: m.name, units: r2(units), qualified: m.hits >= rules.minHits && units > 0, base: 0, bonus: 0, pay: 0, clamp: null, share: 0 };
  });
  const q = rows.filter((r) => r.qualified);
  // The top members by the basis (ties: more units, then name), for the bonus.
  const top = rules.topBonus > 0 && rules.basis !== 'equal' ? [...q].sort((a, b) => b.units - a.units || a.name.localeCompare(b.name)).slice(0, rules.topCount) : [];
  const maxPay = rules.maxPay > 0 ? Math.max(rules.maxPay, rules.minPay) : Infinity;
  let shared = 0;
  let kept = 0;
  if (rules.mode === 'rate') {
    for (const r of q) {
      let v = r.units * rules.rate;
      if (v < rules.minPay) {
        v = rules.minPay;
        r.clamp = 'min';
      }
      if (v > maxPay) {
        v = maxPay;
        r.clamp = 'max';
      }
      r.base = Math.floor(v);
    }
  } else {
    const afterCut = Math.floor((rules.pool * (100 - rules.factionCut)) / 100);
    const bonuses = Math.min(afterCut, top.length * rules.topBonus);
    if (top.length * rules.topBonus > afterCut) notes.push('The bonuses add up to more than the pool — they were cut to fit.');
    shared = afterCut;
    let left = afterCut - bonuses;
    // Water-filling: share `left` by units; members over the cap are fixed at it, under the minimum raised to it.
    const free = new Set(q);
    const fixed = new Map<PayoutRow, number>();
    for (let guard = 0; guard <= q.length && free.size; guard++) {
      const units = [...free].reduce((n, r) => n + r.units, 0);
      if (units <= 0) break;
      const each = (r: PayoutRow) => (left * r.units) / units;
      const over = [...free].filter((r) => each(r) > maxPay);
      if (over.length) {
        for (const r of over) {
          fixed.set(r, maxPay);
          r.clamp = 'max';
          free.delete(r);
          left -= maxPay;
        }
        continue;
      }
      const under = [...free].filter((r) => each(r) < rules.minPay);
      if (under.length) {
        for (const r of under) {
          fixed.set(r, rules.minPay);
          r.clamp = 'min';
          free.delete(r);
          left -= rules.minPay;
        }
        continue;
      }
      for (const r of free) fixed.set(r, each(r));
      free.clear();
    }
    if (left < -0.5) {
      // The minimums don't fit: everyone's share scaled down to the pool.
      notes.push('The minimum payouts add up to more than the pool — everyone was scaled down to fit.');
      const sum = [...fixed.values()].reduce((a, b) => a + b, 0);
      const room = afterCut - bonuses;
      for (const [r, v] of fixed) fixed.set(r, sum > 0 ? (v * room) / sum : 0);
    }
    for (const [r, v] of fixed) r.base = Math.max(0, Math.floor(v));
    const bonusEach = top.length ? Math.floor(bonuses / top.length) : 0;
    for (const r of top) r.bonus = bonusEach;
  }
  if (rules.mode === 'rate') for (const r of top) r.bonus = rules.topBonus;
  for (const r of rows) r.pay = r.base + r.bonus;
  const total = rows.reduce((n, r) => n + r.pay, 0);
  for (const r of rows) r.share = total ? r.pay / total : 0;
  if (rules.mode === 'pool') kept = rules.pool - total;
  if (!q.length && members.length) notes.push(rules.minHits > 0 ? `No one has ${rules.minHits} war hit${rules.minHits === 1 ? '' : 's'} or more.` : 'No one has anything to be paid for.');
  if (rules.basis === 'respect' && members.some((m) => m.respect === null)) notes.push('Respect comes from the attack log; until it’s counted, the war score (respect earned in the war) stands in.');
  return { rows: rows.sort((a, b) => b.pay - a.pay || b.units - a.units || a.name.localeCompare(b.name)), total, kept, shared, notes };
}

/** The payout table as CSV columns (the website's Export CSV). */
export function payoutCsvRows(rec: Pick<WarRecord, 'members'>, res: PayoutResult): { header: string[]; rows: Array<Array<string | number | null>> } {
  const byId = new Map(rec.members.map((m) => [m.id, m]));
  return {
    header: ['Member', 'ID', 'War hits', 'Score', 'Respect', 'Assists', 'Outside hits', 'Hit by enemy', 'Avg FF', 'Units', 'Share %', 'Bonus', 'Payout'],
    rows: res.rows.map((r) => {
      const m = byId.get(r.id);
      return [r.name, r.id, m?.hits ?? 0, m?.score ?? 0, m?.respect ?? null, m?.assists ?? null, m?.outside ?? null, m?.bled ?? null, m?.avgFairFight ?? null, r.units, Math.round(r.share * 10_000) / 100, r.bonus, r.pay];
    }),
  };
}

/** Rules → the `pay=` part of a share link (short: only what differs from the defaults). */
export function encodePayoutRules(r: PayoutRules): string {
  const c = cleanPayoutRules(r);
  const d = DEFAULT_PAYOUT_RULES as unknown as Record<string, unknown>;
  const parts = Object.entries(c)
    .filter(([k, v]) => d[k] !== v)
    .map(([k, v]) => `${k}:${v}`);
  return parts.join(',');
}

/** The `pay=` part of a share link → rules (anything unknown ignored). */
export function decodePayoutRules(s: string | null | undefined): PayoutRules {
  const o: Record<string, string> = {};
  for (const part of String(s ?? '').slice(0, 600).split(',')) {
    const i = part.indexOf(':');
    if (i > 0) o[part.slice(0, i)] = part.slice(i + 1);
  }
  return cleanPayoutRules(o);
}
